import { useEffect, useState } from "react";
import { RefreshCw, Sun } from "lucide-react";
import { api } from "../lib/api";
import { storeName } from "../lib/labels";

const money = (v) => `$${Math.round(v || 0).toLocaleString()}`;
const timeOf = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/Toronto" }) : "");

/** Phone-only: today's sales so far, refreshed from Lightspeed on open. */
export default function TodayBox() {
  const [doc, setDoc] = useState(null);
  const [refreshing, setRefreshing] = useState(true);
  const [err, setErr] = useState("");

  const refresh = () => {
    setRefreshing(true);
    setErr("");
    api.post("/metrics/today/refresh")
      .then((r) => setDoc(r.data))
      .catch(() => {
        setErr("Couldn't reach Lightspeed — showing last synced figures.");
        api.get("/metrics/today").then((r) => setDoc(r.data)).catch(() => {});
      })
      .finally(() => setRefreshing(false));
  };

  useEffect(refresh, []);

  const chain = doc?.chain;
  return (
    <section className="md:hidden border-2 border-[#0a0a0a] bg-[#0a0a0a] text-[#f5f2eb] p-4" data-testid="today-box">
      <div className="flex items-center justify-between gap-3">
        <div className="bm-eyebrow opacity-80 flex items-center gap-2">
          <Sun size={12} className="text-[#c72c2c]" /> Today so far · all stores
        </div>
        <button
          onClick={refresh}
          disabled={refreshing}
          data-testid="today-refresh"
          aria-label="Refresh today's sales"
          className="w-11 h-11 -mr-2 flex items-center justify-center disabled:opacity-60"
        >
          <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="mt-1 flex items-end justify-between gap-3">
        <div className="font-mono-bm text-4xl font-bold tabular-nums" data-testid="today-chain-revenue">
          {chain ? money(chain.revenue) : "—"}
        </div>
        <div className="bm-eyebrow opacity-70 text-right leading-snug">
          {chain ? <>{chain.transactions} sales · {chain.units} units</> : refreshing ? "Pulling today's sales…" : ""}
        </div>
      </div>

      {doc?.stores && (
        <div className="mt-4 border-t border-[#f5f2eb]/20 divide-y divide-[#f5f2eb]/10" data-testid="today-stores">
          {doc.stores.map((s) => (
            <div key={s.outlet_id} className="py-2 flex items-center justify-between gap-3" data-testid={`today-store-${storeName(s.name)}`}>
              <span className="text-sm">{storeName(s.name)}</span>
              <span className="flex items-baseline gap-3">
                <span className="bm-eyebrow opacity-60">{s.transactions} sales</span>
                <span className="font-mono-bm tabular-nums font-semibold w-20 text-right">{money(s.revenue)}</span>
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 bm-eyebrow opacity-60" data-testid="today-as-of">
        {err
          ? err
          : refreshing
            ? "Refreshing from Lightspeed…"
            : doc?.last_refresh
              ? `Fresh from Lightspeed · ${timeOf(doc.last_refresh)} · net of tax`
              : "Net of tax"}
      </div>
    </section>
  );
}
